import React, { useState } from 'react';
import IP_URL from '../connect';
import { useSelector } from 'react-redux';
import { selectUser } from '../../Redux/currentUserSlice';
import styled from 'styled-components';
import Navbar from '../Navbar';
import Sidebar from '../Sidebar';
import Footer from '../Footer';
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const Main = styled.div` background: #f7f9fc; min-height: 100vh; `;
const Container = styled.div`
  margin: 50px auto;
  padding: 40px;
  background: #fff;
  max-width: 500px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
`;
const SectionTitle = styled.h2` text-align: center; color: #2c3e50; margin-bottom: 30px; font-weight: 600; `;
const InputGroup = styled.div`
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  position: relative;
  label { margin-bottom: 6px; font-weight: 500; color: #34495e; }
`;
const Input = styled.input`
  padding: 10px 40px 10px 14px;
  font-size: 14px;
  border-radius: 8px;
  border: 1px solid #ccc;
  transition: 0.3s;
  &:focus { outline: none; border-color: #007bff; box-shadow: 0 0 5px rgba(0,123,255,0.3); }
`;
const ToggleButton = styled.span`
  position: absolute; top: 32px; right: 12px; cursor: pointer; user-select: none; font-size: 14px; color: #007bff;
`;
const Button = styled.button`
  background-color: #007bff;
  color: white;
  width: 100%;
  padding: 12px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: 0.3s;
  &:hover { background-color: #0056b3; }
  &:disabled { background-color: #999; cursor: not-allowed; }
`;
const ErrorMessage = styled.div`
  margin-top: 5px;
  font-size: 12px;
  color: #e74c3c;
  font-weight: 500;
`;

const ChangePassword = () => {
  const { token, email, phone } = useSelector(selectUser);
  const [firstPassword, setFirstPassword] = useState('');
  const [secondPassword, setSecondPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showFirst, setShowFirst] = useState(false);
  const [showSecond, setShowSecond] = useState(false);

  const toggleSidebar = () => setSidebarOpen(prev => !prev);

  // Send OTP (Email or WhatsApp)
  const sendOtp = async (method) => {
    setLoading(true);
    try {
      const res = await fetch(`${IP_URL}/send-otp`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ method, email, phone })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`OTP sent to your ${method === 'email' ? 'email' : 'WhatsApp'}`);
        setOtpSent(true);
      } else {
        toast.error(data.message || "Failed to send OTP");
      }
    } catch (err) {
      toast.error("Network error while sending OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!otp) return toast.error("Please enter the OTP sent to you");
    if (!firstPassword || !secondPassword) return toast.error("Please fill both password fields");
    if (firstPassword !== secondPassword) return toast.error("Passwords do not match");

    setLoading(true);
    try {
      const res = await fetch(`${IP_URL}/verify-otp-and-change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ otp, password: firstPassword })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || "Password changed successfully!");
        setFirstPassword('');
        setSecondPassword('');
        setOtp('');
        setOtpSent(false);
      } else {
        toast.error(data.message || "Invalid OTP or failed to change password");
      }
    } catch (error) {
      toast.error("Error changing password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Main>
      <Navbar showMenuButton toggleSidebar={toggleSidebar} />
      <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <Container>
        <SectionTitle>Change Password</SectionTitle>

        {!otpSent ? (
          <>
            <Button onClick={() => sendOtp('email')} disabled={loading}>
              {loading ? 'Sending...' : 'Send OTP to Email'}
            </Button>
            <div style={{ margin: '10px 0' }} />
            <Button onClick={() => sendOtp('whatsapp')} disabled={loading}>
              {loading ? 'Sending...' : 'Send OTP to WhatsApp'}
            </Button>
          </>
        ) : (
          <>
            <InputGroup>
              <label>Enter OTP</label>
              <Input type="text" placeholder="Enter OTP" value={otp} onChange={(e) => setOtp(e.target.value)} />
            </InputGroup>

            <InputGroup>
              <label>New Password</label>
              <Input
                type={showFirst ? "text" : "password"}
                placeholder="Enter new password"
                value={firstPassword}
                onChange={(e) => setFirstPassword(e.target.value)}
              />
              <ToggleButton onClick={() => setShowFirst(prev => !prev)}>
                {showFirst ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </ToggleButton>
            </InputGroup>

            <InputGroup>
              <label>Confirm New Password</label>
              <Input
                type={showSecond ? "text" : "password"}
                placeholder="Confirm new password"
                value={secondPassword}
                onChange={(e) => setSecondPassword(e.target.value)}
              />
              <ToggleButton onClick={() => setShowSecond(prev => !prev)}>
                {showSecond ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </ToggleButton>
              {secondPassword ? (
                firstPassword !== secondPassword ? (
                  <ErrorMessage>Passwords do not match</ErrorMessage>
                ) : (
                  <ErrorMessage style={{ color: '#2ecc71' }}>Passwords match</ErrorMessage>
                )
              ) : null}
            </InputGroup>

            <Button onClick={handleChangePassword} disabled={loading}>
              {loading ? 'Verifying...' : 'Change Password'}
            </Button>
          </>
        )}
      </Container>
      <Footer />
      <ToastContainer />
    </Main>
  );
};

export default ChangePassword;
